import { useCallback, useEffect, useRef, useState, type CSSProperties } from "react";

import { useMediaQuery } from "../use-media-query";

import type { DerivedPageAssetSlot, SinglePageAssetSlot } from "~/lib/assets/slots";
import { streamMp4Url, streamPosterUrl } from "~/lib/stream/embed";

import { AssetPlaceholder, warnEmptyAssetSlot } from "./AssetPlaceholder";
import { PageVideo } from "./PageVideo";
import { usePageAsset } from "./PageAssetsProvider";

/**
 * T620 — the viewport band that takes the NATIVE portrait master. It is the
 * `home.css` mobile band (`.ho-hero-video { height: 100svh }`), where the promise
 * drops to the calm lower band the 9:16 was framed for. Tablet (768–1023.98px)
 * still frames the hero as a landscape picture, so it keeps the 16:9.
 *
 * One constant, three consumers that must agree: the CSS still (`.ho-hero-video`
 * in `app.css`, T621), the reduced-motion `<picture>` and the master chosen by
 * `useMediaQueryOnce` (T622). The name predates T622 — no `<source>` carries it
 * any more — and is kept because T620/T621 pin it by name.
 */
export const PORTRAIT_SOURCE_MEDIA = "(max-width: 767.98px)";

/** A CSS `url("…")` token; `"` and `\` escaped so a hostile value invalidates nothing. */
const cssUrl = (u: string) => `url("${u.replace(/["\\]/g, "\\$&")}")`;

/*
 * `useMediaQuery` (../use-media-query, shared since T872 S3) reads
 * `prefers-reduced-motion` here — belt-and-suspenders with the CSS `@media` guard
 * (T50): CSS hides the surface on the SSR pass, the hook swaps the client render to a
 * static poster so nothing is fetched or played. NOT used for the still (T621):
 * anything a hook decides arrives after hydration, and a swapped still is a visible
 * frame.
 */

/**
 * T622 — a media query read ONCE, after mount. `null` on the server and on the
 * hydration render (nothing decided yet), then `true`/`false` for the life of
 * the mount — no `change` listener, on purpose: the master of the hero is
 * picked once per visit, the same lifetime the `<source media>` pair had.
 */
function useMediaQueryOnce(query: string): boolean | null {
  const [matches, setMatches] = useState<boolean | null>(null);
  useEffect(() => {
    setMatches(window.matchMedia(query).matches);
  }, [query]);
  return matches;
}

/**
 * T637 — how long a background video may sit without having decoded a single
 * frame before the surface gives up on it and paints the still instead. A
 * refused `play()` rejects and is caught the instant it happens; this bound is
 * for the other shape of the same failure, where the promise resolves and
 * nothing ever appears (a download that never finishes, a decoder that never
 * starts). Both end on `--color-core-dark`, so both end here. 4 s: long enough
 * that a merely slow video still wins its place, short enough that she is not
 * reading the promise over a black rectangle.
 */
export const STILL_FALLBACK_MS = 4000;

/** `HTMLMediaElement.HAVE_CURRENT_DATA` — there is a frame to paint. */
const HAVE_CURRENT_DATA = 2;

/**
 * T637 — attach to a background `<video>` and answer the one question the
 * `autoplay` attribute cannot: did playback actually start? iOS Low Power Mode
 * refuses autoplay outright, Safari draws its own play button over the element,
 * and nothing in the page noticed — the founder's iPhone at 12%, 2026-09-06, on
 * all four landing videos. So the element is played by hand, once per `src`, and
 * the two ways it can end up showing nothing — a rejected promise, or a resolved
 * one with no frame decoded within `STILL_FALLBACK_MS` — both flip `refused`.
 * The first `playing` event cancels the timer: a video that started is never
 * taken away from her.
 */
function usePlaybackRefused(src: string | undefined) {
  const ref = useRef<HTMLVideoElement | null>(null);
  const [refused, setRefused] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (refused || !src || !el) return;

    let live = true;
    const giveUp = () => {
      if (live) setRefused(true);
    };

    const timer = window.setTimeout(() => {
      if (el.readyState < HAVE_CURRENT_DATA) giveUp();
    }, STILL_FALLBACK_MS);
    const onPlaying = () => window.clearTimeout(timer);
    el.addEventListener("playing", onPlaying);

    // The async wrapper is what makes a browser that throws synchronously land
    // in the same place as one that rejects politely — and `play()` returning
    // undefined on an older engine resolves, which is also correct: the timer
    // is then the only judge.
    //
    // Not every rejection is a refusal. `AbortError` means a pending play was
    // interrupted (a new load, a pause) — the video may still start, and the
    // swap is one-way, so reading it as a refusal would strand a working band
    // on its still for the life of the page. Let the timer judge that one; it
    // already judges the resolved-but-never-decoded case. `NotAllowedError`
    // (the T637 refusal) and everything else swap immediately.
    void (async () => {
      await el.play();
    })().catch((err: unknown) => {
      if (err instanceof Error && err.name === "AbortError") return;
      giveUp();
    });

    return () => {
      live = false;
      window.clearTimeout(timer);
      el.removeEventListener("playing", onPlaying);
    };
  }, [src, refused]);

  return { ref, refused };
}

/**
 * T219 — render a managed page video by registry slot in one line.
 * The player's accessible name falls back `title` → `alt` (alt is non-empty
 * for published assets, R-T218-4); an empty or non-video slot renders the
 * documented placeholder instead.
 *
 * `background` switches from the interactive Stream iframe player to a native
 * `<video>` for decorative, full-bleed hero use: muted/autoplay/loop, no
 * controls, `object-fit: cover` (the consumer's CSS owns sizing). It is sourced
 * from the Stream MP4 (`streamMp4Url`) with the CF auto-thumbnail as the poster,
 * so it shows a still immediately and starts looping once the video's downloads
 * are enabled. It is `aria-hidden` — decorative only, never the page's content.
 *
 * T620 — `portraitSlot` names a second, video-only placement holding the NATIVE
 * portrait master of the same scene. When the portrait slot is empty, the
 * markup is exactly the single-`src` one (R-T620-1).
 *
 * T621 — the still of a two-master hero is CSS's choice, never JavaScript's.
 * `<video poster>` is one URL with no media query, and a poster swapped after
 * hydration is a landscape frame the phone paints first and then jumps away from
 * (reproduced on production, 2026-09-05). So the two-master `<video>` carries no
 * `poster`; it exposes both stills as custom properties — `--slot-poster-landscape`
 * and `--slot-poster-portrait` — and the consumer's CSS paints the right one as
 * `background-image` under the same `PORTRAIT_SOURCE_MEDIA` band from the first
 * paint (`.ho-hero-video` in `app.css`; mirror `background-position` from
 * `object-position` so the still and the first frame share the crop). The video
 * box is transparent until its first frame, so the still shows through exactly
 * as a poster would.
 *
 * T622 — the MASTER of a two-master hero is chosen once, by script, never by a
 * `<source>` chain. T620 shipped the pair as `<source media>` + bare `<source>`;
 * the browser's resource selection is a fallback chain, not a selector: it runs
 * once, never re-evaluates `media`, evaluates it at an instant the page does not
 * control (WebKit reads it against the parent's render style and answers `false`
 * when there is none yet), and moves on to the next candidate whenever the first
 * fails to fetch — so a phone could end up playing the landscape master under the
 * portrait still, with nothing able to notice or correct it (founder report,
 * 2026-09-05). Now the SSR `<video>` carries neither `<source>` nor `src`; after
 * mount `useMediaQueryOnce(PORTRAIT_SOURCE_MEDIA)` sets exactly one `src`. The
 * master starts after hydration, but the still (T621) is already on screen on the
 * same crop, so the later first frame is not a jump. The reduced-motion `<picture>`
 * keeps `<source media>`: the image algorithm IS a selector and re-evaluates.
 */
export type SlotVideoProps = {
  slot: SinglePageAssetSlot | DerivedPageAssetSlot;
  className?: string;
  /** Decorative full-bleed background mode (native muted/autoplay/loop video). */
  background?: boolean;
  /** T620 — registry slot of the native portrait master (background mode only). */
  portraitSlot?: SinglePageAssetSlot;
  /**
   * T872 S2 — the sensory band of the October delivery (DECISIONES.md): the video plays
   * once when it enters the viewport, for at most `SENSORY_MAX_SECONDS`, never loops,
   * pauses when it leaves, and shows the still under reduced motion or on failure.
   * Background mode only; no pause button.
   */
  playOnce?: boolean;
};

/** T872 S2 — how long the sensory band may play: one breath, not a loop. */
export const SENSORY_MAX_SECONDS = 4;

/**
 * T872 S2 — the sensory band's run: start on the first intersection (20 %), cap at
 * `SENSORY_MAX_SECONDS`, stop on `ended`, pause when scrolled out. `refuse` is called
 * when `play()` rejects (iOS Low Power Mode and the like) so the caller paints the still.
 *
 * S3 QA (2026-10-09): the run is DONE when the four seconds have been consumed (or the
 * video ends), not when it first started — a fast scroll through the band used to leave
 * it frozen at 0.002 s for good. Leaving mid-run pauses; coming back resumes from where
 * it paused, until the cap. Never a loop: once done, nothing restarts it.
 */
function useSensoryPlayback(
  ref: React.RefObject<HTMLVideoElement | null>,
  enabled: boolean,
  refuse: () => void,
) {
  useEffect(() => {
    const el = ref.current;
    if (!enabled || !el) return;
    let started = false;
    let finished = false;
    // Paused by a scroll-out, with seconds still to consume: the next entry resumes.
    let parked = false;
    // The media query resolves after the first paint, so a reduced-motion swap can unmount
    // this element with an observation still queued: a disposed run never calls play().
    let disposed = false;
    const stop = () => {
      finished = true;
      el.pause();
    };
    const onTime = () => {
      if (el.currentTime >= SENSORY_MAX_SECONDS) stop();
    };
    const run = () => {
      void (async () => {
        await el.play();
      })().catch((err: unknown) => {
        if (err instanceof Error && err.name === "AbortError") return;
        refuse();
      });
    };
    el.addEventListener("timeupdate", onTime);
    el.addEventListener("ended", stop);
    const observer =
      typeof IntersectionObserver === "function"
        ? new IntersectionObserver(
            (entries) => {
              if (finished || disposed) return;
              const visible = entries[0]?.isIntersecting ?? false;
              if (!visible) {
                // Mid-run: pause, keep the position; the run is not over.
                if (started) {
                  parked = true;
                  el.pause();
                }
                return;
              }
              if (started && !parked) return;
              started = true;
              parked = false;
              run();
            },
            { threshold: 0.2 },
          )
        : null;
    observer?.observe(el);
    return () => {
      disposed = true;
      el.removeEventListener("timeupdate", onTime);
      el.removeEventListener("ended", stop);
      observer?.disconnect();
    };
  }, [ref, enabled, refuse]);
}

type BackgroundVideoProps = {
  className?: string;
  /** Stream uid of the landscape master — the only one, when there is only one. */
  landscapeUid: string;
  /** T620 — stream uid of the native portrait master, or `null` when unpublished. */
  portraitUid: string | null;
  /** T872 S2 — see `SlotVideoProps.playOnce`. */
  playOnce?: boolean;
};

/**
 * The `background` branch, extracted (T637) so its own hooks run AFTER the empty-slot
 * guard above: `usePlaybackRefused` needs the `src` the guard is what makes knowable,
 * and a hook cannot live behind an early return. Nothing about the rendered markup
 * moved — every SSR byte is the one T620/T621/T622 pinned.
 */
function BackgroundVideo({ className, landscapeUid, portraitUid, playOnce = false }: BackgroundVideoProps) {
  const reducedMotion = useMediaQuery("(prefers-reduced-motion: reduce)");
  const portraitBand = useMediaQueryOnce(PORTRAIT_SOURCE_MEDIA);
  // T872 S2 — the sensory band refuses on its own terms (no autoplay to judge).
  const [sensoryRefused, setSensoryRefused] = useState(false);
  const refuseSensory = useCallback(() => setSensoryRefused(true), []);

  const posterOf = (uid: string) => streamPosterUrl(uid, { time: "1s", height: 1080 });
  const poster = posterOf(landscapeUid);

  // One master: the `src` is known on the server. Two (T622): it waits for the band.
  const master = !portraitUid
    ? streamMp4Url(landscapeUid)
    : portraitBand === null
      ? undefined
      : streamMp4Url(portraitBand ? portraitUid : landscapeUid);

  // Reduced motion mounts no <video> at all, so there is nothing to play. The sensory
  // band does not autoplay either: its run starts on intersection (`useSensoryPlayback`).
  const { ref, refused: autoplayRefused } = usePlaybackRefused(
    reducedMotion || playOnce ? undefined : master,
  );
  useSensoryPlayback(ref, playOnce && !reducedMotion && !!master, refuseSensory);
  const refused = autoplayRefused || sensoryRefused;

  // Reduced motion → a static poster <img>: no <video> in the DOM at all, so
  // nothing buffers and there is no motion. Decorative (alt="" + aria-hidden).
  if (reducedMotion) {
    if (!portraitUid) {
      return <img className={className} src={poster} alt="" aria-hidden={true} />;
    }
    // Native per-viewport still: <picture> picks the portrait poster on the phone band.
    return (
      <picture>
        <source media={PORTRAIT_SOURCE_MEDIA} srcSet={posterOf(portraitUid)} />
        <img className={className} src={poster} alt="" aria-hidden={true} />
      </picture>
    );
  }

  // T637 — playback did not start. The scene is shown as the still the band would
  // have painted anyway, and the element that cannot paint it leaves the DOM: an
  // empty <video> is where Safari draws its own play button over `--color-core-dark`.
  // Same render shape reduced motion has used since T50 — decorative, same class.
  if (refused) {
    const still = portraitUid && portraitBand ? posterOf(portraitUid) : poster;
    return <img className={className} src={still} alt="" aria-hidden={true} />;
  }

  // `preload="metadata"` (not "auto"): a decorative asset must not pull
  // several MB on every cold load.
  if (!portraitUid) {
    return (
      <video
        ref={ref}
        className={className}
        src={master}
        poster={poster}
        autoPlay={!playOnce}
        muted
        loop={!playOnce}
        playsInline
        preload="metadata"
        disableRemotePlayback
        aria-hidden={true}
      />
    );
  }
  // Two native masters (T622): no `src` until the band is known — the server and
  // the hydration render emit none, so there is nothing for the browser to pick
  // or fall back to — then exactly one. The consumer's CSS resolves the still
  // from these two custom properties (T621) — no `poster`, nothing to swap.
  const stills = {
    "--slot-poster-landscape": cssUrl(poster),
    "--slot-poster-portrait": cssUrl(posterOf(portraitUid)),
  } as CSSProperties;
  return (
    <video
      ref={ref}
      className={className}
      style={stills}
      src={master}
      autoPlay={!playOnce}
      muted
      loop={!playOnce}
      playsInline
      preload="metadata"
      disableRemotePlayback
      aria-hidden={true}
    />
  );
}

export function SlotVideo({ slot, className, background, portraitSlot, playOnce }: SlotVideoProps) {
  const asset = usePageAsset(slot);
  // Hooks run unconditionally: with no `portraitSlot` this re-reads `slot` and the
  // result is discarded below, so the render never branches on hook order.
  const portraitCandidate = usePageAsset(portraitSlot ?? slot);

  if (!asset || asset.kind !== "video" || !asset.streamUid) {
    warnEmptyAssetSlot(slot, asset ? "no renderable video for this slot" : "no published asset");
    return <AssetPlaceholder className={className} />;
  }

  if (background) {
    const portraitUid =
      portraitSlot && portraitCandidate?.kind === "video" && portraitCandidate.streamUid
        ? portraitCandidate.streamUid
        : null;
    return (
      <BackgroundVideo className={className} landscapeUid={asset.streamUid} portraitUid={portraitUid} playOnce={playOnce}
      />
    );
  }

  return <PageVideo uid={asset.streamUid} title={asset.title ?? asset.alt} className={className} />;
}
